import { Info } from "lucide-react";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { CustomEventApplyToUrlsDto, UrlMatch } from "@intelligems/ig-types";
import { Grid, IconButton } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useGetShopDomain } from "@src/api/shops/get-domain";
import { AddAnotherButton } from "@src/components/Utils/AddAnotherButton";
import { CustomEventUrl } from "@src/features/customEvents/components/CustomEventUrl";
import {
  selectedCurrentCustomEvent,
  setCustomEventUrls,
} from "@src/store/slices/customEvents";
import { isZeroWidthRegex } from "@src/utils/regex";

interface CustomEventPageTargetingWrapperProps {
  buttonText: string;
  showHeader?: boolean;
  showInitial?: boolean;
  isArchived: boolean;
}

export const CustomEventPageTargetingWrapper = ({
  buttonText,
  isArchived,
  showHeader = false,
  showInitial = false,
}: CustomEventPageTargetingWrapperProps) => {
  const domain = useGetShopDomain();
  const theme = useTheme();
  const currentEvent = useSelector(selectedCurrentCustomEvent);
  const dispatch = useDispatch();
  const [selectedUrls, setSelectedUrls] = useImmer<CustomEventApplyToUrlsDto[]>(
    currentEvent?.applyToUrls?.length && currentEvent?.id
      ? currentEvent.applyToUrls
      : showInitial
        ? [
            {
              matchBy: "contains",
              value: "",
            },
          ]
        : []
  );

  const tooltipText =
    "By default, events trigger on any page of your site. If you want the event to trigger only on specific pages, list those pages here.";

  const handleMatchChange = (index: number, newValue: UrlMatch) => {
    setSelectedUrls((draft) => {
      draft[index].matchBy = newValue;
      draft[index].error = null;
    });
  };

  const handleUrlChange = (index: number, newValue: string) => {
    setSelectedUrls((draft) => {
      draft[index].value = newValue;
      if (draft[index].matchBy === "regex") {
        if (newValue) {
          draft[index].error = isZeroWidthRegex(newValue)
            ? "Invalid Regex Expression: zero-width expressions are not supported."
            : "";
        } else {
          draft[index].error = null;
        }
      }
    });
  };

  const handleAddUrl = () => {
    setSelectedUrls((draft) => {
      draft.push({
        matchBy: "contains",
        value: "",
      });
    });
  };

  const handleRemoveUrl = (index: number) => {
    setSelectedUrls((draft) => {
      draft.splice(index, 1);
    });
  };

  useEffect(() => {
    dispatch(
      setCustomEventUrls({
        urls: selectedUrls,
      })
    );
  }, [selectedUrls]);

  return (
    <Grid width={"100%"} marginTop={showHeader ? "0px" : "10px"}>
      {showHeader && (
        <Grid container alignItems={"center"} marginBottom={"10px"}>
          <IgTypography fontWeight={600} variant={"h3"}>
            Do you want to limit it to only certain pages?
          </IgTypography>
          <IgTooltip
            title={tooltipText}
            placement={"right"}
            sx={{ width: "250px" }}
          >
            <IconButton size={"small"}>
              <Info size={16} />
            </IconButton>
          </IgTooltip>
        </Grid>
      )}
      {selectedUrls.map((url, index) => (
        <CustomEventUrl
          key={index}
          url={url}
          index={index}
          isArchived={isArchived}
          onMatchChange={handleMatchChange}
          onUrlChange={handleUrlChange}
          onRemove={handleRemoveUrl}
          domain={domain?.data || ""}
          error={url.error}
        />
      ))}
      {!isArchived && (
        <AddAnotherButton
          itemLabel={buttonText}
          onAddClick={handleAddUrl}
          isDisabled={selectedUrls.length >= 5}
        />
      )}
    </Grid>
  );
};
